import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { modelService, uploadService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { Zap, Database, ArrowRight, ChevronRight, ChevronDown, Sparkles, FlaskConical, FileText, RefreshCw, ChevronLeft, CheckCircle, Bot, Cpu, X, Server } from 'lucide-react';
import ModeToggle from '../components/ui/ModeToggle';
import ChatWithArtifacts from '../components/chat/ChatWithArtifacts';

// Format file size for display
const formatFileSize = (sizeInBytes) => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  } else if (sizeInBytes < 1024 * 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  } else {
    return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
};

const MODEL_PROVIDERS = [
  { id: 'openai/gpt-4o-mini', name: 'OpenAI GPT-4o Mini', description: 'Fast, cost-effective model for simple tasks', icon: <Zap size={20} className="text-primary-500" /> },
  { id: 'openai/gpt-4o', name: 'OpenAI GPT-4o', description: 'Powerful reasoning with latest capabilities', icon: <Zap size={20} className="text-primary-600" /> },
  { id: 'anthropic/claude-3-haiku', name: 'Anthropic Claude 3 Haiku', description: 'Quick, efficient model for lightweight tasks', icon: <Bot size={20} className="text-primary-500" /> },
  { id: 'anthropic/claude-3-sonnet', name: 'Anthropic Claude 3 Sonnet', description: 'Strong reasoning with nuanced understanding', icon: <Bot size={20} className="text-primary-600" /> },
  { id: 'anthropic/claude-3.7-sonnet', name: 'Anthropic Claude 3.7 Sonnet', description: 'Latest generation with advanced reasoning', icon: <Bot size={20} className="text-primary-700" /> },
];

function ModelCreation() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify } = useNotification();
  const { darkMode } = useTheme();
  const intentRef = useRef(null);
  
  // Get session ID from URL parameters
  const sessionId = searchParams.get('session');
  
  // Current step
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Primary state
  const [modelName, setModelName] = useState('');
  const [intent, setIntent] = useState('');
  const [isNameInferred, setIsNameInferred] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [expandedDatasets, setExpandedDatasets] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState({}); // {datasetName: versionUploadId}
  const [provider, setProvider] = useState('openai/gpt-4o');
  
  // Schema state
  const [inputSchema, setInputSchema] = useState([{ field: '', type: 'string', arrayElementType: 'string', description: '' }]);
  const [outputSchema, setOutputSchema] = useState([{ field: '', type: 'string', arrayElementType: 'string', description: '' }]);
  const [autoInferSchema, setAutoInferSchema] = useState(true);
  
  // Deployment state
  const [autoDeploy, setAutoDeploy] = useState(true);
  
  // Advanced settings
  const [maxIterations, setMaxIterations] = useState(2);
  const [timeout, setTimeout] = useState(7200); // Default to 120 minutes
  
  // UI states
  const [creatingModel, setCreatingModel] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [interfaceMode, setInterfaceMode] = useState('traditional');
  
  // Dataset states
  const [datasets, setDatasets] = useState({});
  
  // Get dataset info for an upload (similar to DataSources page)
  const getDatasetInfo = (upload) => {
    let datasetName = upload.dataset_name;
    
    // If no explicit dataset_name, check if this upload_id appears in any dataset
    if (!datasetName) {
      for (const [name, versions] of Object.entries(datasets)) {
        if (versions.some(v => v.upload_id === upload.upload_id)) {
          datasetName = name;
          break;
        }
      }
    }
    
    if (!datasetName) return null;
    
    const datasetVersions = datasets[datasetName] || [];
    const versionCount = datasetVersions.length;
    const isLatest = upload.is_latest;

    return {
      datasetName,
      hasVersions: versionCount > 1,
      versionCount,
      isLatest
    };
  };

  // Function to manually generate a model name based on intent
  const generateModelName = () => {
    if (intent) {
      // Extract key words and create a kebab-case model name
      const words = intent.toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove special chars
        .split(/\s+/) // Split by whitespace
        .filter(word => word.length > 2 && !['and', 'the', 'for', 'with'].includes(word)) // Filter out common words
        .slice(0, 3); // Take first 3 significant words
      
      if (words.length > 0) {
        setModelName(words.join('-'));
        setIsNameInferred(true);
      }
    }
  };

  // Fetch available uploads when on data step
  useEffect(() => {
    const fetchUploads = async () => {
      if (currentStep === 1 && !uploads.length && !loadingUploads) {
        try {
          setLoadingUploads(true);
          const data = await uploadService.getUploads();
          setUploads(data);
          
          // Also fetch datasets for versioning and name lookup
          const datasetData = await uploadService.getDatasets();
          setDatasets(datasetData.datasets || datasetData || {});
          
          
          // No need to auto-select uploads anymore - users can select from multiple uploads
        } catch (error) {
          console.error('Error fetching uploads:', error);
          notify.error('Failed to load data sources');
        } finally {
          setLoadingUploads(false);
        }
      }
    };

    fetchUploads();
  }, [currentStep, uploads.length, loadingUploads, notify]);

  // Set default schemas and model
  useEffect(() => {
    // Default to gpt-4o as the model provider
    setProvider('openai/gpt-4o');
  }, []);
  
  // Handle input schema field changes
  const handleInputFieldChange = (index, field, value) => {
    const newInputSchema = [...inputSchema];
    newInputSchema[index][field] = value;
    
    // Reset arrayElementType if type changes from array
    if (field === 'type' && value !== 'array') {
      newInputSchema[index]['arrayElementType'] = 'string';
    }
    
    setInputSchema(newInputSchema);
  };
  
  // Handle output schema field changes
  const handleOutputFieldChange = (index, field, value) => {
    const newOutputSchema = [...outputSchema];
    newOutputSchema[index][field] = value;
    
    // Reset arrayElementType if type changes from array
    if (field === 'type' && value !== 'array') {
      newOutputSchema[index]['arrayElementType'] = 'string';
    }
    
    setOutputSchema(newOutputSchema);
  };
  
  // Add new field to input schema
  const addInputField = () => {
    setInputSchema([...inputSchema, { field: '', type: 'string', arrayElementType: 'string', description: '' }]);
  };
  
  // Add new field to output schema
  const addOutputField = () => {
    setOutputSchema([...outputSchema, { field: '', type: 'string', arrayElementType: 'string', description: '' }]);
  };
  
  // Remove field from input schema
  const removeInputField = (index) => {
    if (inputSchema.length > 1) {
      const newInputSchema = [...inputSchema];
      newInputSchema.splice(index, 1);
      setInputSchema(newInputSchema);
    }
  };
  
  // Remove field from output schema
  const removeOutputField = (index) => {
    if (outputSchema.length > 1) {
      const newOutputSchema = [...outputSchema];
      newOutputSchema.splice(index, 1);
      setOutputSchema(newOutputSchema);
    }
  };

  // Validate all inputs before submission
  const validateAllInputs = () => {
    const errors = {};
    
    if (!intent.trim()) {
      errors.intent = "Please describe what you want your model to do";
    }
    
    if (!modelName.trim()) {
      errors.modelName = "Please provide a name for your model";
    } else if (!/^[a-z0-9-]+$/.test(modelName)) {
      errors.modelName = "Model name can only contain lowercase letters, numbers, and hyphens";
    }
    
    // Check if any datasets are selected
    if (selectedDatasets.length === 0) {
      errors.data = "Please select at least one dataset";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step with animation
  const handleNextStep = useCallback(() => {
    // Validate inputs for current step - defined inside useCallback to avoid dependency issues
    const validateCurrentStep = () => {
      const errors = {};
      
      switch (currentStep) {
        case 0: // Intent
          if (!intent.trim()) {
            errors.intent = "Please describe what you want your model to do";
          }
          if (!modelName.trim()) {
            errors.modelName = "Please provide a name for your model";
          } else if (!/^[a-z0-9-]+$/.test(modelName)) {
            errors.modelName = "Model name can only contain lowercase letters, numbers, and hyphens";
          }
          break;
          
        case 1: // Data
          // Check if any datasets are selected
          if (selectedDatasets.length === 0) {
            errors.data = "Please select at least one dataset";
          }
          break;
          
        case 2: // Deployment
          // No validation required for deployment step
          break;
          
        default:
          // Default case for the switch statement
          break;
      }
      
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    if (validateCurrentStep() && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    }
  }, [currentStep, intent, modelName, selectedDatasets, isAnimating]);

  // Handle previous step with animation
  const handlePrevStep = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => Math.max(0, prev - 1));
        setIsAnimating(false);
      }, 300);
    }
  }, [isAnimating]);

  // Create the model
  const handleCreateModel = async () => {
    if (!validateAllInputs()) {
      // Find the first step with errors and go there
      if (validationErrors.intent || validationErrors.modelName) {
        setCurrentStep(0);
      } else if (validationErrors.data) {
        setCurrentStep(1);
      }
      return;
    }
    
    try {
      setCreatingModel(true);
      
      // Build request based on ModelRequest from the API
      let modelData = {};
      
      // Transform selectedDatasets to dataset_refs format
      const datasetRefsPayload = selectedDatasets.map(versionKey => {
        // Find the actual version from the datasets structure
        let selectedVersion = null;
        let datasetName = null;
        
        for (const [name, versions] of Object.entries(datasets)) {
          const version = versions.find(v => {
            const versionMatch = v.data_dirs?.[0]?.match(/\/versions\/(v\d+)\//);
            const versionNumber = versionMatch ? versionMatch[1] : 'v0';
            return `${v.upload_id}-${versionNumber}` === versionKey;
          });
          if (version) {
            selectedVersion = version;
            datasetName = name;
            break;
          }
        }
        
        if (!selectedVersion) {
          return null;
        }
        
        // Extract version from data_dirs path
        const versionMatch = selectedVersion.data_dirs?.[0]?.match(/\/versions\/(v\d+)\//);
        const version = versionMatch ? versionMatch[1] : 'v0';
        
        return {
          dataset_id: selectedVersion.upload_id,
          version: version
        };
      }).filter(Boolean);
      
      modelData = {
        dataset_refs: datasetRefsPayload,
        intent: intent,
        provider: provider,
        max_iterations: parseInt(maxIterations),
        timeout: parseInt(timeout) || 1800,
        auto_deploy: autoDeploy,
        model_name: modelName
      };
      
      // Add the auto-infer flag
      modelData.auto_infer_schema = autoInferSchema;
      
      // Only add schemas if not auto-inferring
      if (!autoInferSchema) {
        // Helper function to map UI types to backend Python types
        const mapToBackendType = (type, arrayElementType) => {
          if (type === 'array') {
            switch (arrayElementType) {
              case 'string': return 'List[str]';
              case 'integer': return 'List[int]';
              case 'number': return 'List[float]';
              case 'boolean': return 'List[bool]';
              default: return 'List[str]';
            }
          }
          
          switch (type) {
            case 'string': return 'str';
            case 'integer': return 'int';
            case 'number': return 'float';
            case 'boolean': return 'bool';
            default: return 'str';
          }
        };
        
        // Convert to new backend format: { "field_name": ["field_type", "field_description"] }
        const formattedInputSchema = {};
        inputSchema.forEach(field => {
          if (field.field.trim()) {
            formattedInputSchema[field.field] = [
              mapToBackendType(field.type, field.arrayElementType),
              field.description || ''
            ];
          }
        });
        
        const formattedOutputSchema = {};
        outputSchema.forEach(field => {
          if (field.field.trim()) {
            formattedOutputSchema[field.field] = [
              mapToBackendType(field.type, field.arrayElementType),
              field.description || ''
            ];
          }
        });
        
        if (Object.keys(formattedInputSchema).length > 0) {
          modelData.input_schema = formattedInputSchema;
        }
        
        if (Object.keys(formattedOutputSchema).length > 0) {
          modelData.output_schema = formattedOutputSchema;
        }
      }
      
      const response = await modelService.createModel(modelName, modelData);
      
      navigate(`/models/${response.model_version}`);
    } catch (error) {
      console.error('Error creating model:', error);
      
      // Check if it's a CORS error - these actually succeed on the backend
      if (error.message && error.message.includes('CORS') || 
          error.message && error.message.includes('Network Error') ||
          (error.response === undefined && error.request)) {
        // CORS error but request likely succeeded - navigate to models page
        notify.success('Model creation started successfully');
        navigate('/models');
      } else {
        notify.error('Failed to create model: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setCreatingModel(false);
    }
  };

  // Handle dataset expansion (clicking on dataset name/header)
  const handleDatasetExpansion = (datasetName) => {
    setExpandedDatasets(prev => {
      const isExpanded = prev.includes(datasetName);
      if (isExpanded) {
        // Collapse - remove from expanded and selected
        setSelectedDatasets(prevSelected => 
          prevSelected.filter(id => {
            const upload = uploads.find(u => u.upload_id === id);
            return getDatasetInfo(upload)?.datasetName !== datasetName;
          })
        );
        setSelectedVersions(prevVersions => {
          const newVersions = {...prevVersions};
          delete newVersions[datasetName];
          return newVersions;
        });
        return prev.filter(name => name !== datasetName);
      } else {
        // Just expand, don't auto-select anything
        return [...prev, datasetName];
      }
    });
  };

  // Handle version selection within expanded dataset
  const handleVersionSelection = (datasetName, versionKey, version) => {
    // Remove ALL previous versions for this dataset from selectedDatasets
    const datasetVersions = datasets[datasetName] || [];
    const allVersionKeys = datasetVersions.map(v => {
      const versionMatch = v.data_dirs?.[0]?.match(/\/versions\/(v\d+)\//);
      const versionNumber = versionMatch ? versionMatch[1] : 'v0';
      return `${v.upload_id}-${versionNumber}`;
    });
    
    setSelectedDatasets(prev => prev.filter(id => !allVersionKeys.includes(id)));
    
    // Add new version selection
    setSelectedDatasets(prev => [...prev, versionKey]);
    
    setSelectedVersions(prev => ({
      ...prev,
      [datasetName]: versionKey
    }));
  };

  // Render data step
  const renderDataStep = () => (
    <div className="step-content">
      <h2 className="step-title">Select your training data</h2>
      <p className="step-description">
        Choose your training data source. Expand datasets to select specific versions.
      </p>
      
      {loadingUploads ? (
        <div className="loading-state">
          <RefreshCw className="animate-spin h-6 w-6 text-blue-500" />
          <p>Loading your data sources...</p>
        </div>
      ) : Object.keys(datasets).length > 0 ? (
        <div className="data-sources">
          {Object.entries(datasets)
            // Filter to only show datasets with at least one ready version
            .filter(([datasetName, versions]) =>
              versions.some(v => v.status === 'ready' || v.status === undefined || v.status === 'unknown')
            )
            .map(([datasetName, versions]) => {
            const isExpanded = expandedDatasets.includes(datasetName);
            const selectedVersionId = selectedVersions[datasetName];
            const isDatasetSelected = selectedVersionId !== undefined;

            return (
            <div key={datasetName} className={`data-source-container ${isDatasetSelected ? 'selected' : ''}`}>
              <div 
                className="data-source-header"
                onClick={() => handleDatasetExpansion(datasetName)}
              >
                <div className="data-icon" style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}>
                  <Database size={20} className="text-green-600 dark:text-green-400" />
                </div>
                <div className="data-content">
                  <h3 className="data-name dark:text-slate-200">
                    {datasetName}
                  </h3>
                  <p className="data-meta dark:text-slate-400">
                    {versions.length} version{versions.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="data-expand">
                  {isExpanded ? 
                    <ChevronDown size={16} /> : 
                    <ChevronRight size={16} />
                  }
                </div>
              </div>
              
              {isExpanded && (
                <div className="dataset-versions">
                  {versions
                    // Filter to only show ready versions (or unknown/undefined for backward compatibility)
                    .filter(v => v.status === 'ready' || v.status === undefined || v.status === 'unknown')
                    .map((version) => {
                    // Extract version from data_dirs path like /versions/v0/ or /versions/v1/
                    const versionMatch = version.data_dirs?.[0]?.match(/\/versions\/(v\d+)\//);
                    const versionNumber = versionMatch ? versionMatch[1] : 'v0';
                    const versionKey = `${version.upload_id}-${versionNumber}`;
                    const isVersionSelected = selectedVersionId === versionKey;
                    return (
                      <div 
                        key={versionKey}
                        className={`version-item ${isVersionSelected ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVersionSelection(datasetName, versionKey, version);
                        }}
                      >
                        <div className="version-content">
                          <div className="version-info">
                            <span className="version-name">
                              {version.version_description || `Version ${version.upload_id.substring(0, 8)}`}
                            </span>
                            {version.is_latest && (
                              <span className="latest-badge">Latest</span>
                            )}
                          </div>
                          <p className="version-date">
                            {version.created_at && new Date(version.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className={`version-check ${isVersionSelected ? 'visible' : ''}`}>
                          <CheckCircle size={16} className="text-green-500" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <Database size={40} className="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400 mb-6">No data sources available yet</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/data')}
          >
            Upload Data Now
          </button>
        </div>
      )}
      
      {validationErrors.data && (
        <div className="validation-error">{validationErrors.data}</div>
      )}
    </div>
  );

  // Render intent step
  const renderIntentStep = () => (
    <div className="step-content max-w-2xl mx-auto">
      <h2 className="step-title">Describe your model's purpose</h2>
      <p className="step-description">
        Explain what you want your model to do in detail. Be specific about what you want to predict and what data it should use.
      </p>
      
      <div className="form-group">
        <label htmlFor="intent" className="form-label flex items-center">
          <FlaskConical size={16} className="text-primary-500 mr-2" />
          <span>Model Intent</span>
        </label>
        <textarea
          ref={intentRef}
          id="intent"
          rows={5}
          className={`form-textarea w-full bg-white text-gray-900 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded-md px-4 py-3 ${validationErrors.intent ? 'border-red-300' : ''}`}
          style={{ 
            backgroundColor: darkMode ? '#334155' : 'white', 
            color: darkMode ? 'white' : '#111827', 
            colorScheme: darkMode ? 'dark' : 'light'
          }}
          placeholder="Describe what you want your model to do (e.g., predict customer churn based on purchase history and support interactions)"
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
        />
        {validationErrors.intent && (
          <div className="validation-error">{validationErrors.intent}</div>
        )}
      </div>
      
      <div className="form-group">
        <label htmlFor="model-name" className="form-label flex items-center mb-2">
          <FileText size={16} className="text-primary-500 mr-2" />
          <span>Model Name</span>
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              id="model-name"
              className={`form-input pl-8 w-full bg-white text-gray-900 border border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white ${validationErrors.modelName ? 'border-red-300' : ''}`}
              style={{ 
                backgroundColor: darkMode ? '#334155' : 'white', 
                color: darkMode ? 'white' : '#111827', 
                colorScheme: darkMode ? 'dark' : 'light'
              }}
              placeholder="e.g., customer-churn-predictor"
              value={modelName}
              onChange={(e) => {
                setModelName(e.target.value);
                setIsNameInferred(false); // Mark as user-entered when manually typed
              }}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <code className="text-slate-400 text-xs">/</code>
            </div>
          </div>
          <button
            type="button"
            onClick={generateModelName}
            disabled={!intent.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-500 dark:hover:bg-primary-600 flex items-center gap-2 whitespace-nowrap"
          >
            <Sparkles size={16} />
            Generate
          </button>
        </div>
        {validationErrors.modelName && (
          <div className="validation-error">{validationErrors.modelName}</div>
        )}
        <p className="input-hint">
          A unique identifier for your model. Use lowercase letters, numbers, and hyphens only.
        </p>
      </div>
    </div>
  );

  // Render deployment step
  const renderDeploymentStep = () => (
    <div className="step-content px-8 py-6">
      <h2 className="step-title">Configure Deployment</h2>
      <p className="step-description mb-8">
        Configure how your model will be deployed and define its input and output schema.
      </p>
      
      <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 mb-10">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Deployment Options</h3>
        
        <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-md border border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <label className="switch mr-3">
                <input
                  type="checkbox"
                  checked={autoDeploy}
                  onChange={(e) => setAutoDeploy(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Auto-Deploy</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {autoDeploy ? 'Enabled' : 'Manual deployment'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-4"></div>
              <Server size={18} className="mr-2 text-primary-500" />
              <div className="text-sm">
                <div className="font-medium text-gray-900 dark:text-white">API Endpoint</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Schema Configuration</h3>
          <div className="flex items-center">
            <span className="text-sm mr-3 text-gray-700 dark:text-gray-300">Auto-infer Schema:</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={autoInferSchema}
                onChange={(e) => setAutoInferSchema(e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>
        
        {autoInferSchema ? (
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-6 text-center">
            <Sparkles className="h-8 w-8 text-primary-500 dark:text-primary-400 mx-auto mb-4" />
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 font-medium">
              Schema will be automatically inferred from your data source.
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              The model will analyze your data and create appropriate input and output fields.
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            <div className="w-full">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Input Schema</h4>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/50 font-medium"
                  onClick={addInputField}
                >
                  + Add Field
                </button>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-5">
                <div className="space-y-4">
                  {inputSchema.map((field, index) => (
                    <div key={index} className="flex flex-col space-y-3 p-3 bg-white dark:bg-slate-700 rounded-md border border-gray-200 dark:border-gray-600">
                      <div className="flex gap-4 items-center">
                        <input
                          type="text"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                          placeholder="Field name"
                          value={field.field}
                          onChange={(e) => handleInputFieldChange(index, 'field', e.target.value)}
                        />
                        <select
                          value={field.type}
                          onChange={(e) => handleInputFieldChange(index, 'type', e.target.value)}
                          className="w-36 px-4 py-3 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="string">string</option>
                          <option value="integer">integer</option>
                          <option value="number">number</option>
                          <option value="boolean">boolean</option>
                          <option value="array">array</option>
                        </select>
                        {inputSchema.length > 1 && (
                          <button 
                            type="button"
                            className="p-3 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors"
                            onClick={() => removeInputField(index)}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {field.type === 'array' && (
                        <div className="ml-4 mt-2">
                          <select 
                            value={field.arrayElementType} 
                            onChange={(e) => handleInputFieldChange(index, 'arrayElementType', e.target.value)}
                            className="w-40 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="string">of strings</option>
                            <option value="integer">of integers</option>
                            <option value="number">of numbers</option>
                            <option value="boolean">of booleans</option>
                          </select>
                        </div>
                      )}
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Describe this field..."
                        rows={2}
                        value={field.description}
                        onChange={(e) => handleInputFieldChange(index, 'description', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="w-full">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Output Schema</h4>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/50 font-medium"
                  onClick={addOutputField}
                >
                  + Add Field
                </button>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-5">
                <div className="space-y-4">
                  {outputSchema.map((field, index) => (
                    <div key={index} className="flex flex-col space-y-3 p-3 bg-white dark:bg-slate-700 rounded-md border border-gray-200 dark:border-gray-600">
                      <div className="flex gap-4 items-center">
                        <input
                          type="text"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                          placeholder="Field name"
                          value={field.field}
                          onChange={(e) => handleOutputFieldChange(index, 'field', e.target.value)}
                        />
                        <select
                          value={field.type}
                          onChange={(e) => handleOutputFieldChange(index, 'type', e.target.value)}
                          className="w-36 px-4 py-3 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="string">string</option>
                          <option value="integer">integer</option>
                          <option value="number">number</option>
                          <option value="boolean">boolean</option>
                          <option value="array">array</option>
                        </select>
                        {outputSchema.length > 1 && (
                          <button 
                            type="button"
                            className="p-3 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors"
                            onClick={() => removeOutputField(index)}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {field.type === 'array' && (
                        <div className="ml-4 mt-2">
                          <select 
                            value={field.arrayElementType} 
                            onChange={(e) => handleOutputFieldChange(index, 'arrayElementType', e.target.value)}
                            className="w-40 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="string">of strings</option>
                            <option value="integer">of integers</option>
                            <option value="number">of numbers</option>
                            <option value="boolean">of booleans</option>
                          </select>
                        </div>
                      )}
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Describe this field..."
                        rows={2}
                        value={field.description}
                        onChange={(e) => handleOutputFieldChange(index, 'description', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-6 text-center bg-gray-50 dark:bg-slate-800/50 rounded-md p-4">
          {autoInferSchema 
            ? "The model builder will automatically infer schema based on your data."
            : "Define the custom schema to structure your model's inputs and outputs."}
        </p>
      </div>
    </div>
  );

  // Render review step
  const renderReviewStep = () => (
    <div className="step-content">
      <h2 className="step-title">Review and configure</h2>
      <p className="step-description">
        Review your settings and make any final adjustments before creating your model.
      </p>
      
      <div className="review-section justify-between items-center px-4">
      <h3 className="section-title py-2">   Model Overview</h3>
        
        <div className="review-item">
          <div className="review-label">Intent</div>
          <div className="review-value">{intent || '—'}</div>
        </div>
        
        <div className="review-item">
          <div className="review-label">Model Name</div>
          <div className="review-value font-mono">{modelName || '—'}</div>
        </div>
        
        <div className="review-item">
          <div className="review-label">Data Source</div>
          <div className="review-value">
            {selectedDatasets.length > 0 ? (
              <div>
                {Object.entries(selectedVersions).map(([datasetName, versionUploadId]) => {
                  // Find the version details
                  const datasetVersions = datasets[datasetName] || [];
                  const version = datasetVersions.find(v => v.upload_id === versionUploadId);
                  const versionName = version?.version_description || `Version ${versionUploadId.substring(0, 8)}`;
                  
                  return (
                  <div key={versionUploadId} className="mb-2">
                    <span className="flex items-center mb-1">
                      <Database size={16} className="mr-2" />
                      {datasetName}
                    </span>
                    <div className="text-xs text-slate-600 dark:text-slate-300 ml-6">
                      {versionName}
                      {version?.is_latest && <span className="text-green-600 ml-2">(Latest)</span>}
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : '—'}
          </div>
        </div>
        
        <div className="review-item">
          <div className="review-label">Deployment</div>
          <div className="review-value">
            <span className="flex items-center">
              <Server size={16} className="mr-2 text-primary-500" />
              <span>{autoDeploy ? 'Auto-deploy enabled' : 'Manual deployment'}</span>
            </span>
          </div>
        </div>
      </div>
      
      <div className="review-section">
        <div className="flex justify-between items-center px-4">
          <h3 className="section-title py-2">Advanced Settings</h3>
          <button
            type="button"
            className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 px-2 py-1"
            onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
          >
            {isAdvancedExpanded ? 'Hide' : 'Advanced'}
          </button>
        </div>
        
        {isAdvancedExpanded && (
          <>
            <div className="review-item">
              <div className="review-label">Experiments</div>
              <div className="review-value">
                <div className="flex items-center">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={maxIterations}
                    onChange={(e) => setMaxIterations(e.target.value)}
                    className="form-range flex-1"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700 min-w-[2rem] text-center">{maxIterations}</span>
                </div>
                <p className="input-hint">Number of refinement experiments (1-5)</p>
              </div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Timeout</div>
              <div className="review-value">
                <select
                  value={timeout}
                  onChange={(e) => setTimeout(e.target.value)}
                  className="form-select w-full"
                >
                  <option value="600">10 minutes</option>
                  <option value="1800">30 minutes</option>
                  <option value="3600">1 hour</option>
                  <option value="7200">2 hours</option>
                </select>
                <p className="input-hint">Maximum build time allowed</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );


  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderIntentStep();
      case 1:
        return renderDataStep();
      case 2:
        return renderDeploymentStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Create form state object for chat context
  const formState = {
    currentStep,
    modelName,
    intent,
    selectedDatasets,
    provider,
    inputSchema,
    outputSchema,
    autoInferSchema,
    autoDeploy,
    maxIterations,
    timeout,
    uploads,
    validationErrors
  };

  const handleFormUpdate = (updates) => {
    Object.entries(updates).forEach(([key, value]) => {
      switch (key) {
        case 'modelName':
          setModelName(value);
          break;
        case 'intent':
          setIntent(value);
          break;
        case 'selectedDatasets':
          setSelectedDatasets(value);
          break;
        case 'provider':
          setProvider(value);
          break;
        case 'currentStep':
          setCurrentStep(value);
          break;
        default:
          break;
      }
    });
  };

  // Main component render
  return (
    <div className={`flex flex-col ${interfaceMode === 'traditional' ? 'max-w-4xl mx-auto' : 'w-full max-w-none'}`} style={{ height: 'calc(100vh - 120px)' }}>
      <div className={`mb-6 flex justify-between items-center flex-shrink-0 ${interfaceMode === 'chat' ? 'max-w-4xl mx-auto w-full px-4' : ''}`}>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Model</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Build a custom AI model for your specific needs in a few simple steps
          </p>
        </div>
        
        <ModeToggle 
          mode={interfaceMode} 
          onModeChange={setInterfaceMode}
          disabled={creatingModel}
        />
      </div>
      
      {/* Conditional rendering based on mode */}
      {interfaceMode === 'traditional' ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-visible">
        {/* Progress indicators with content */}
        <div className="flex flex-row relative">
          <div className="flex-1 p-8">
            <div className={`step-content-wrapper ${isAnimating ? 'animating' : ''}`}>
              {renderStepContent()}
            </div>
            
            <div className="step-actions">
              <div className="flex justify-between w-full">
                <div>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600 inline-flex items-center"
                      onClick={handlePrevStep}
                    >
                      <ChevronLeft size={16} className="mr-1" />
                      Back
                    </button>
                  )}
                </div>
                
                <div>
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center"
                      onClick={handleNextStep}
                    >
                      Next
                      <ArrowRight size={16} className="ml-1" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
                      onClick={handleCreateModel}
                      disabled={creatingModel}
                    >
                      {creatingModel ? (
                        <>
                          <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Create Model
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Progress indicators now positioned absolutely for better centering */}
        <div className="progress-track-vertical">
          {[
            { label: "Purpose", icon: <FlaskConical size={12} className="text-blue-500" /> },
            { label: "Data", icon: <Database size={12} className="text-green-500" /> },
            { label: "Deployment", icon: <Cpu size={12} className="text-purple-500" /> },
            { label: "Review", icon: <CheckCircle size={12} className="text-amber-500" /> }
          ].map((step, idx) => (
            <div 
              key={idx}
              className={`progress-step-vertical ${currentStep === idx ? 'active' : ''} ${currentStep > idx ? 'completed' : ''}`}
              onClick={() => {
                // Only allow clicking on completed steps or the current step
                if (currentStep > idx || currentStep === idx) {
                  setCurrentStep(idx);
                }
              }}
            >
              <div className="step-indicator-vertical">
                {step.icon}
              </div>
              <div className="step-label-vertical">{step.label}</div>
            </div>
          ))}
          <div className="progress-line-vertical">
            <div 
              className="progress-fill-vertical" 
              style={{ height: currentStep === 0 ? '0%' : currentStep === 1 ? '33%' : currentStep === 2 ? '66%' : '100%' }}
            ></div>
          </div>
        </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 overflow-hidden">
          <ChatWithArtifacts
            formState={formState}
            onFormUpdate={handleFormUpdate}
            sessionId={sessionId}
          />
        </div>
      )}
      
      <style jsx="true">{`
        /* Main container styles */
        .model-creation {
          /* Using Tailwind max-w-6xl mx-auto instead */
        }
        
        .creation-container {
          /* Using Tailwind classes for styling now */
        }
        
        .step-content-wrapper {
          position: relative;
          transition: opacity 0.3s ease-out;
        }
        
        .step-content-wrapper.animating {
          opacity: 0;
        }
        
        /* Apple-style layout */
        .apple-flow-layout {
          /* Using Tailwind flex classes now */
        }
        
        .content-canvas {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        
        .progress-track-vertical {
          width: 30px;
          position: absolute;
          top: 50%;
          right: 20px;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 180px;
          background: transparent;
          z-index: 10;
        }
        
        .progress-step-vertical {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          position: relative;
          z-index: 5;
          transition: all 0.4s cubic-bezier(0.19, 1, 0.22, 1);
          margin-bottom: 38px;
        }
        
        .progress-step-vertical:last-child {
          margin-bottom: 0;
        }
        
        .step-indicator-vertical {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #e2e8f0;
          transition: all 0.3s cubic-bezier(0.19, 1, 0.22, 1);
          border: 1px solid #e5e7eb;
          font-size: 0.6rem;
          opacity: 0.4;
          transform: scale(0.8);
        }
        
        .progress-step-vertical.active .step-indicator-vertical {
          background: white;
          color: #3b82f6;
          border-color: #3b82f6;
          box-shadow: 0 1px 4px rgba(59, 130, 246, 0.2);
          transform: scale(1);
          opacity: 1;
        }
        
        .progress-step-vertical.completed .step-indicator-vertical {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
          opacity: 0.6;
          transform: scale(0.9);
        }
        
        .step-label-vertical {
          display: none;
        }
        
        .progress-line-vertical {
          position: absolute;
          top: 34px;
          left: 15px;
          width: 1px;
          height: 112px; /* Exact height to connect first and last step */
          background: #e5e7eb;
          opacity: 0.2;
          z-index: 1;
        }
        
        .progress-fill-vertical {
          width: 100%;
          background: #60a5fa;
          opacity: 0.5;
          transition: height 0.5s cubic-bezier(0.19, 1, 0.22, 1);
          position: absolute;
          bottom: 0;
        }
        
        /* Apple-style buttons */
        .apple-btn {
          transition: all 0.3s ease;
          border-radius: 8px;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 100px;
          padding: 10px 16px;
        }
        
        .apple-btn.btn-primary {
          background: #3b82f6;
          border: none;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
          color: white;
        }
        
        .apple-btn.btn-primary:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        
        .apple-btn.btn-secondary {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        
        .apple-btn.btn-secondary:hover {
          background: #f1f5f9;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        }
        
        @media (prefers-color-scheme: dark) {
          .creation-container {
            background: rgb(var(--card-background));
            border-color: rgb(var(--border-color));
          }
          
          .progress-track-vertical {
            background: rgba(15, 23, 42, 0.7);
            border-left-color: #334155;
          }
          
          .step-indicator-vertical {
            background: #1e293b;
            border-color: #475569;
            color: #94a3b8;
          }
          
          .progress-step-vertical.active .step-indicator-vertical {
            background: #0f172a;
            border-color: #3b82f6;
            box-shadow: 0 2px 10px rgba(59, 130, 246, 0.4);
          }
          
          .progress-line-vertical {
            background: #334155;
          }
          
          .apple-btn.btn-secondary {
            background: #1e293b;
            border-color: #475569;
            color: #e2e8f0;
          }
          
          .apple-btn.btn-secondary:hover {
            background: #334155;
          }
        }
        
        .dark .creation-container {
          background: rgb(var(--card-background));
          border-color: rgb(var(--border-color));
        }
        
        .dark .progress-track-vertical {
          background: rgba(15, 23, 42, 0.7);
          border-left-color: #334155;
        }
        
        .dark .step-indicator-vertical {
          background: #1e293b;
          border-color: #475569;
          color: #94a3b8;
        }
        
        .dark .progress-step-vertical.active .step-indicator-vertical {
          background: #0f172a;
          border-color: #3b82f6;
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.4);
        }
        
        .dark .progress-line-vertical {
          background: #334155;
        }
        
        .dark .apple-btn.btn-secondary {
          background: #1e293b;
          border-color: #475569;
          color: #e2e8f0;
        }
        
        .dark .apple-btn.btn-secondary:hover {
          background: #334155;
        }
        
        /* Content styles to match Apple design language */
        .step-content {
          max-width: 580px;
          margin: 0 auto;
          animation: fadeInStep 0.5s cubic-bezier(0.19, 1, 0.22, 1);
        }
        
        .step-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 12px;
          color: #111827;
          letter-spacing: -0.025em;
        }
        
        .step-description {
          color: #6b7280;
          font-size: 0.95rem;
          margin-bottom: 36px;
          line-height: 1.5;
        }
        
        .form-group {
          margin-bottom: 24px;
        }
        
        .form-label {
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
          font-size: 0.9rem;
          letter-spacing: -0.01em;
        }
        
        .form-input, .form-textarea {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          background: #fcfcfc;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        
        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
          background: #fff;
        }
        
        @keyframes fadeInStep {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideIn {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        /* Dark mode adaptations for Apple aesthetics */
        @media (prefers-color-scheme: dark) {
          .step-title {
            color: #f9fafb;
          }
          
          .step-description {
            color: #9ca3af;
          }
          
          .form-label {
            color: #d1d5db;
          }
          
          .form-input, .form-textarea {
            border-color: #4b5563;
            background: rgba(17, 24, 39, 0.8);
            color: #e5e7eb;
          }
          
          .form-input:focus, .form-textarea:focus {
            border-color: #60a5fa;
            background: rgba(17, 24, 39, 1);
            box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2);
          }
        }
        
        .dark .step-title {
          color: #f9fafb;
        }
        
        .dark .step-description {
          color: #9ca3af;
        }
        
        .dark .form-label {
          color: #d1d5db;
        }
        
        .dark .form-input, .dark .form-textarea {
          border-color: #4b5563;
          background: #1e293b;
          color: #e5e7eb;
        }
        
        .dark .form-input:focus, .dark .form-textarea:focus {
          border-color: #60a5fa;
          background: #1e293b;
          box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2);
        }
        
        /* DS Team replacement UI components */
        .ds-replacement-header {
          display: flex;
          align-items: center;
          margin-bottom: 20px;
          padding: 12px 16px;
          background: linear-gradient(to right, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0) 100%);
          border-left: 3px solid #3b82f6;
          border-radius: 6px;
        }
        
        .ds-role-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .ds-replacement-marker {
          position: absolute;
          top: 0;
          right: 0;
          font-size: 0.7rem;
          background: linear-gradient(to right, #3b82f6, #8b5cf6);
          color: white;
          padding: 3px 8px;
          border-radius: 4px;
          transform: translateY(-50%);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          z-index: 1;
        }
        
        .ds-info-panel {
          background: rgba(59, 130, 246, 0.05);
          border: 1px solid rgba(59, 130, 246, 0.1);
          border-radius: 8px;
          padding: 16px;
          margin-top: 24px;
        }
        
        .ds-info-panel-header {
          display: flex;
          align-items: center;
          font-weight: 500;
          color: #3b82f6;
          margin-bottom: 12px;
          font-size: 0.9rem;
          gap: 8px;
        }
        
        .ds-info-list {
          list-style-type: none;
          padding: 0;
          margin: 0;
        }
        
        .ds-info-list li {
          padding-left: 20px;
          margin-bottom: 8px;
          position: relative;
          font-size: 0.85rem;
          color: #64748b;
        }
        
        .ds-info-list li:before {
          content: "•";
          position: absolute;
          left: 6px;
          color: #3b82f6;
          font-weight: bold;
        }
        
        .dark .ds-replacement-header {
          background: linear-gradient(to right, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.02) 100%);
          border-left-color: #3b82f6;
        }
        
        .dark .ds-replacement-text span:last-child {
          color: #e2e8f0;
        }
        
        .dark .ds-info-panel {
          background: rgba(59, 130, 246, 0.05);
          border-color: rgba(59, 130, 246, 0.2);
        }
        
        .dark .ds-info-list li {
          color: #94a3b8;
        }
        
        /* Apple-style heading */
        .apple-heading {
          font-size: 2rem;
          font-weight: 600;
          letter-spacing: -0.025em;
          color: #111827;
        }
        
        .apple-subheading {
          font-size: 1rem;
          line-height: 1.5;
          color: #6b7280;
        }
        
        @media (prefers-color-scheme: dark) {
          .apple-heading {
            color: #f9fafb;
          }
          
          .apple-subheading {
            color: #9ca3af;
          }
        }
        
        .dark .apple-heading {
          color: #f9fafb;
        }
        
        .dark .apple-subheading {
          color: #9ca3af;
        }
        
        /* DS Team Badge */
        .ds-team-badge {
          display: flex;
          align-items: center;
          padding: 6px 12px;
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(139, 92, 246, 0.1));
          border: 1px solid rgba(56, 189, 248, 0.2);
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #3b82f6;
          transition: all 0.2s ease;
          box-shadow: 0 2px 5px rgba(59, 130, 246, 0.1);
          animation: pulseShadow 2s infinite alternate;
        }
        
        .ds-team-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.15), rgba(139, 92, 246, 0.15));
        }
        
        @keyframes pulseShadow {
          from { box-shadow: 0 2px 5px rgba(59, 130, 246, 0.1); }
          to { box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2); }
        }
        
        .dark .ds-team-badge {
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.15), rgba(139, 92, 246, 0.15));
          border-color: rgba(56, 189, 248, 0.3);
          color: #60a5fa;
        }
        
        /* File selection styles */
        .data-sources {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .data-source-container {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: var(--panel-shadow);
          cursor: pointer;
        }
        
        .data-source-container.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
        }
        
        .data-source-header {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #f8fafc;
        }
        
        .data-source-header:hover {
          background: #f1f5f9;
        }
        
        .data-source-header.expanded {
          background: #f0f9ff;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .data-content {
          flex: 1;
        }
        
        .data-expand {
          margin-left: 12px;
          color: #64748b;
        }
        
        .data-files-expanded {
          padding: 12px 16px;
          background: white;
        }
        
        .files-list {
          max-height: 250px;
          overflow-y: auto;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }
        
        .file-item {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }
        
        .file-item:last-child {
          border-bottom: none;
        }
        
        .file-item:hover {
          background: #f8fafc;
        }
        
        .file-item.selected {
          background: #f0f9ff;
        }
        
        .file-icon {
          margin-right: 10px;
          color: #64748b;
        }
        
        .file-details {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .file-name {
          font-size: 0.875rem;
          color: #334155;
          margin-bottom: 2px;
        }
        
        .file-size {
          font-size: 0.75rem;
          color: #64748b;
        }
        
        .file-check {
          color: #3b82f6;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .file-check.visible {
          opacity: 1;
        }
        
        /* Dark mode styles for data sources */
        @media (prefers-color-scheme: dark) {
          .data-source-container {
            border-color: #334155;
            background: rgb(var(--card-background));
          }
          
          .data-source-header {
            background: rgba(30, 41, 59, 0.7);
          }
          
          .data-source-header:hover {
            background: rgba(30, 41, 59, 0.9);
          }
          
          .data-source-header.expanded {
            background: rgba(29, 78, 216, 0.2);
            border-bottom-color: #334155;
          }
          
          .data-expand {
            color: #94a3b8;
          }
          
          .data-files-expanded {
            background: rgb(var(--card-background));
          }
          
          .files-list {
            border-color: #334155;
            background: rgba(15, 23, 42, 0.5);
          }
          
          .file-item {
            border-bottom-color: #334155;
          }
          
          .file-item:hover {
            background: rgba(30, 41, 59, 0.5);
          }
          
          .file-item.selected {
            background: rgba(29, 78, 216, 0.2);
          }
          
          .file-icon {
            color: #94a3b8;
          }
          
          .file-name {
            color: #e2e8f0;
          }
          
          .file-size {
            color: #94a3b8;
          }
          
          .file-check {
            color: #60a5fa;
          }
        }
        
        .dark .data-source-container {
          border-color: #334155;
          background: rgb(var(--card-background));
        }
        
        .dark .data-source-container.selected {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }
        
        .dataset-check {
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .dataset-check.visible {
          opacity: 1;
        }
        
        .dataset-versions {
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .version-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .version-item:last-child {
          border-bottom: none;
        }
        
        .version-item:hover {
          background: #f1f5f9;
        }
        
        .version-item.selected {
          background: #dbeafe;
          border-left: 3px solid #3b82f6;
        }
        
        .version-content {
          flex: 1;
        }
        
        .version-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        
        .version-name {
          font-weight: 500;
          color: #1e293b;
        }
        
        .latest-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          background: #dcfce7;
          color: #16a34a;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        
        .version-date {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }
        
        .version-check {
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .version-check.visible {
          opacity: 1;
        }
        
        .dark .data-source-header {
          background: rgba(30, 41, 59, 0.7);
        }
        
        .dark .dataset-versions {
          border-color: #334155;
          background: rgba(15, 23, 42, 0.5);
        }
        
        .dark .version-item {
          border-color: #334155;
        }
        
        .dark .version-item:hover {
          background: rgba(30, 41, 59, 0.5);
        }
        
        .dark .version-item.selected {
          background: rgba(59, 130, 246, 0.1);
          border-left-color: #3b82f6;
        }
        
        .dark .version-name {
          color: #f1f5f9;
        }
        
        .dark .latest-badge {
          background: rgba(34, 197, 94, 0.2);
          color: #4ade80;
        }
        
        .dark .version-date {
          color: #94a3b8;
        }
        
        .dark .data-source-header:hover {
          background: rgba(30, 41, 59, 0.9);
        }
        
        .dark .data-source-header.expanded {
          background: rgba(29, 78, 216, 0.2);
          border-bottom-color: #334155;
        }
        
        .dark .data-expand {
          color: #94a3b8;
        }
        
        .dark .data-files-expanded {
          background: rgb(var(--card-background));
        }
        
        .dark .files-list {
          border-color: #334155;
          background: rgba(15, 23, 42, 0.5);
        }
        
        .dark .file-item {
          border-bottom-color: #334155;
        }
        
        .dark .file-item:hover {
          background: rgba(30, 41, 59, 0.5);
        }
        
        .dark .file-item.selected {
          background: rgba(29, 78, 216, 0.2);
        }
        
        .dark .file-icon {
          color: #94a3b8;
        }
        
        .dark .file-name {
          color: #e2e8f0;
        }
        
        .dark .file-size {
          color: #94a3b8;
        }
        
        .dark .file-check {
          color: #60a5fa;
        }
        
        /* Progress track with DS team replacement visualization */
        .progress-track {
          display: flex;
          padding: 16px 24px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 25%;
          position: relative;
          z-index: 5;
          cursor: pointer;
        }
        
        .step-indicator {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          margin-bottom: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: var(--panel-shadow);
          position: relative;
          overflow: hidden;
        }
        
        .step-indicator::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.1);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .step-indicator:hover::after {
          opacity: 1;
        }
        
        .step-indicator .robot-icon {
          width: 28px;
          height: 28px;
          transition: all 0.3s ease;
          filter: grayscale(0.8);
        }
        
        .progress-step.active .step-indicator {
          transform: scale(1.1);
          background: var(--step-color, #3b82f6);
          color: white;
          box-shadow: 0 4px 14px rgba(var(--step-color-rgb, 59, 130, 246), 0.4);
        }
        
        .progress-step.active .step-indicator .robot-icon {
          filter: grayscale(0);
        }
        
        .progress-step.completed .step-indicator {
          background: var(--step-color, #3b82f6);
          color: white;
          box-shadow: 0 2px 6px rgba(var(--step-color-rgb, 59, 130, 246), 0.3);
        }
        
        .progress-step.completed .step-indicator .robot-icon {
          filter: grayscale(0);
        }
        
        .step-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          transition: all 0.2s ease;
          text-align: center;
        }
        
        .progress-step.active .step-label {
          color: #1e293b;
          font-weight: 600;
        }
        
        .progress-step.completed .step-label {
          color: var(--step-color, #3b82f6);
        }
        
        .ds-role {
          font-size: 0.7rem;
          color: #94a3b8;
          margin-top: 2px;
          opacity: 0.7;
          text-align: center;
          max-width: 90px;
        }
        
        .progress-step.active .ds-role {
          opacity: 1;
          color: #64748b;
        }
        
        .progress-line {
          position: absolute;
          top: 42px;
          left: 10%;
          width: 80%;
          height: 2px;
          background: #e2e8f0;
          z-index: 1;
        }
        
        .progress-fill {
          height: 100%;
          background: linear-gradient(to right, 
            rgba(56, 189, 248, 0.8),
            rgba(139, 92, 246, 0.8),
            rgba(6, 182, 212, 0.8)
          );
          transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 0 8px rgba(56, 189, 248, 0.5);
        }
        
        /* Dark mode progress track */
        @media (prefers-color-scheme: dark) {
          .progress-track {
            background: #1e293b;
            border-bottom-color: #334155;
          }
          
          .step-indicator {
            background: #334155;
            color: #94a3b8;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
          
          .progress-step.active .step-indicator {
            background: #2563eb;
            color: white;
            box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
          }
          
          .progress-step.completed .step-indicator {
            background: #059669;
            color: white;
            box-shadow: 0 2px 6px rgba(5, 150, 105, 0.3);
          }
          
          .step-label {
            color: #94a3b8;
          }
          
          .progress-step.active .step-label {
            color: #e2e8f0;
          }
          
          .progress-step.completed .step-label {
            color: #34d399;
          }
          
          .progress-line {
            background: #334155;
          }
          
          .progress-fill {
            background: #059669;
          }
        }
        
        .dark .progress-track {
          background: #1e293b;
          border-bottom-color: #334155;
        }
        
        .dark .step-indicator {
          background: #334155;
          color: #94a3b8;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .dark .progress-step.active .step-indicator {
          background: #2563eb;
          color: white;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
        }
        
        .dark .progress-step.completed .step-indicator {
          background: #059669;
          color: white;
          box-shadow: 0 2px 6px rgba(5, 150, 105, 0.3);
        }
        
        .dark .step-label {
          color: #94a3b8;
        }
        
        .dark .progress-step.active .step-label {
          color: #e2e8f0;
        }
        
        .dark .progress-step.completed .step-label {
          color: #34d399;
        }
        
        .dark .progress-line {
          background: #334155;
        }
        
        .dark .progress-fill {
          background: #059669;
        }
        
        /* Step container */
        .step-container {
          padding: 32px 40px;
        }
        
        /* Custom scrollbar for Apple style */
        .step-content::-webkit-scrollbar {
          width: 8px;
        }
        
        .step-content::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .step-content::-webkit-scrollbar-thumb {
          background-color: rgba(203, 213, 225, 0.5);
          border-radius: 20px;
          border: 2px solid transparent;
        }
        
        .step-content::-webkit-scrollbar-thumb:hover {
          background-color: rgba(148, 163, 184, 0.7);
        }
        
        .dark .step-content::-webkit-scrollbar-thumb {
          background-color: rgba(71, 85, 105, 0.5);
        }
        
        .dark .step-content::-webkit-scrollbar-thumb:hover {
          background-color: rgba(100, 116, 139, 0.7);
        }
        
        .step-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
        }
        
        .step-description {
          color: #64748b;
          margin-bottom: 24px;
        }
        
        .step-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #f1f5f9;
          width: 100%;
          max-width: 520px;
          margin-left: auto;
          margin-right: auto;
        }
        
        /* Objective selection */
        .objective-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .objective-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        
        .objective-card:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        
        .objective-card.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
        }
        
        .objective-icon {
          margin-bottom: 12px;
        }
        
        .objective-name {
          font-weight: 600;
          font-size: 0.9375rem;
          color: #334155;
          margin-bottom: 4px;
        }
        
        .objective-description {
          font-size: 0.875rem;
          color: #64748b;
        }
        
        .objective-check {
          position: absolute;
          top: 12px;
          right: 12px;
          color: #3b82f6;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .objective-check.visible {
          opacity: 1;
        }
        
        /* Data selection */
        .data-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .data-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          display: flex;
        }
        
        .data-card:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        
        .data-card.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
        }
        
        .data-icon {
          width: 36px;
          height: 36px;
          background: #f1f5f9;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          margin-right: 12px;
        }
        
        .data-card.selected .data-icon {
          background: #3b82f6;
          color: white;
        }
        
        .data-content {
          flex: 1;
        }
        
        .data-name {
          font-weight: 600;
          font-size: 0.9375rem;
          color: #334155;
        }
        
        .data-meta {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 2px;
        }
        
        .data-files {
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px dashed #e2e8f0;
        }
        
        .data-check {
          position: absolute;
          top: 12px;
          right: 12px;
          color: #3b82f6;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .data-check.visible {
          opacity: 1;
        }
        
        /* Intent form */
        .form-group {
          margin-bottom: 20px;
        }
        
        .form-label {
          display: block;
          font-weight: 500;
          color: #334155;
          margin-bottom: 6px;
        }
        
        .form-textarea {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 0.875rem;
          resize: vertical;
          min-height: 100px;
          background: #fff;
          transition: all 0.2s ease;
          box-shadow: var(--panel-shadow);
        }
        
        .form-textarea:focus, .form-input:focus, .form-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
        
        .form-textarea.error, .form-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .form-input {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 0.875rem;
          background: #fff;
          transition: all 0.2s ease;
          box-shadow: var(--panel-shadow);
        }
        
        .input-hint {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 4px;
        }
        
        /* Dark mode forms */
        @media (prefers-color-scheme: dark) {
          .form-label {
            color: #e2e8f0;
          }
          
          .form-textarea, .form-input {
            border-color: #475569;
            background: #1e293b;
            color: #e2e8f0;
          }
          
          .form-textarea:focus, .form-input:focus, .form-select:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
          }
          
          .form-textarea.error, .form-input.error {
            border-color: #ef4444;
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
          }
          
          .input-hint {
            color: #94a3b8;
          }
        }
        
        .dark .form-label {
          color: #e2e8f0;
        }
        
        .dark .form-textarea, .dark .form-input {
          border-color: #475569;
          background: #1e293b;
          color: #e2e8f0;
        }
        
        .dark .form-textarea:focus, .dark .form-input:focus, .dark .form-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }
        
        .dark .form-textarea.error, .dark .form-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
        }
        
        .dark .input-hint {
          color: #94a3b8;
        }
        
        /* Review step */
        .review-section {
          margin-bottom: 28px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: var(--panel-shadow);
        }
        
        .section-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #334155;
          padding: 12px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        
        /* Add special case for section titles in flex containers */
        .flex .section-title {
          padding: 12px 0;
          background: transparent;
          border-bottom: none;
        }
        
        .review-item {
          display: flex;
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .review-item:last-child {
          border-bottom: none;
        }
        
        .review-label {
          width: 120px;
          font-size: 0.875rem;
          color: #64748b;
          flex-shrink: 0;
        }
        
        .review-value {
          flex: 1;
          font-size: 0.875rem;
          color: #334155;
        }
        
        /* Dark mode review section */
        @media (prefers-color-scheme: dark) {
          .review-section {
            border-color: #334155;
            background: rgb(var(--card-background));
          }
          
          .section-title {
            color: #e2e8f0;
            background: #1e293b;
            border-bottom-color: #334155;
          }
          
          .flex .section-title {
            background: transparent;
            border-bottom: none;
          }
          
          .review-item {
            border-bottom-color: #334155;
          }
          
          .review-label {
            color: #94a3b8;
          }
          
          .review-value {
            color: #e2e8f0;
          }
        }
        
        .dark .review-section {
          border-color: #334155;
          background: rgb(var(--card-background));
        }
        
        .dark .section-title {
          color: #e2e8f0;
          background: #1e293b;
          border-bottom-color: #334155;
        }
        
        .dark .flex .section-title {
          background: transparent;
          border-bottom: none;
        }
        
        .dark .review-item {
          border-bottom-color: #334155;
        }
        
        .dark .review-label {
          color: #94a3b8;
        }
        
        .dark .review-value {
          color: #e2e8f0;
        }
        
        /* Provider selection */
        .provider-selector {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
        }
        
        .provider-option {
          display: flex;
          align-items: center;
          padding: 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .provider-option:hover {
          background: #f8fafc;
        }
        
        .provider-option.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
        }
        
        .provider-icon {
          margin-right: 10px;
        }
        
        .provider-name {
          font-size: 0.875rem;
          font-weight: 500;
          color: #334155;
        }
        
        .provider-description {
          font-size: 0.75rem;
          color: #64748b;
        }
        
        /* Dark mode provider styles */
        .dark .provider-name {
          color: #e2e8f0;
        }
        
        .dark .provider-description {
          color: #94a3b8;
        }
        
        /* Schema display */
        .schemas-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          padding: 20px;
          max-height: 460px; /* Increased container height */
          overflow-y: auto;
        }
        
        .schema-title {
          font-size: 0.875rem;
          font-weight: 500;
          color: #334155;
          margin-bottom: 8px;
        }
        
        .schema-box {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #f8fafc;
          padding: 12px;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: var(--panel-shadow);
        }
        
        .schema-code {
          font-family: monospace;
          font-size: 0.75rem;
          white-space: pre-wrap;
          color: #334155;
        }
        
        /* Dark mode schema display */
        @media (prefers-color-scheme: dark) {
          .schema-title {
            color: #e2e8f0;
          }
          
          .schema-box {
            border-color: #334155;
            background: #1e293b;
          }
          
          .schema-code {
            color: #e2e8f0;
          }
        }
        
        .dark .schema-title {
          color: #e2e8f0;
        }
        
        .dark .schema-box {
          border-color: #334155;
          background: #1e293b;
        }
        
        .dark .schema-code {
          color: #e2e8f0;
        }
        
        /* Loading and empty states */
        .loading-state, .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 0;
          text-align: center;
        }
        
        .loading-state p, .empty-state p {
          margin: 16px 0;
          color: #64748b;
        }
        
        /* Validation errors */
        .validation-error {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 4px;
        }
        
        /* Buttons */
        .btn {
          padding: 10px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        
        .btn-primary {
          background: #3b82f6;  /* primary-500 */
          color: white;
          border: 1px solid #2563eb;  /* primary-600 */
        }
        
        .btn-primary:hover {
          background: #2563eb;  /* primary-600 */
        }
        
        .btn-primary:disabled {
          background: #93c5fd;  /* primary-300 - light blue */
          border-color: #818cf8;  /* primary-400 */
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        .btn-secondary {
          background: white;
          color: #334155;
          border: 1px solid #cbd5e1;
        }
        
        .btn-secondary:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }
        
        /* Form elements */
        .form-range {
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          appearance: none;
        }
        
        .form-range::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #6366f1;  /* primary-500 */
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }
        
        .form-select {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 0.875rem;
          background: #fff;
          appearance: none;
          box-shadow: var(--panel-shadow);
          background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
          background-repeat: no-repeat;
          background-position: right 10px center;
          background-size: 16px;
        }
        
        /* Dark mode form elements */
        @media (prefers-color-scheme: dark) {
          .form-range {
            background: #334155;
          }
          
          .form-range::-webkit-slider-thumb {
            background: #4f46e5;  /* primary-600 */
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
          }
          
          .form-select {
            border-color: #475569;
            background-color: #1e293b;
            color: #e2e8f0;
            background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
          }
        }
        
        .dark .form-range {
          background: #334155;
        }
        
        .dark .form-range::-webkit-slider-thumb {
          background: #4f46e5;  /* primary-600 */
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        }
        
        .dark .form-select {
          border-color: #475569;
          background-color: #1e293b;
          color: #e2e8f0;
          background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
        }
        
        /* Schema field styles */
        .schema-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .schema-fields {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 16px;
          background: #f8fafc;
          max-height: 350px;
          overflow-y: auto;
          box-shadow: var(--panel-shadow);
        }
        
        .schema-field-row {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          gap: 10px;
        }
        
        .schema-field-row:last-child {
          margin-bottom: 0;
        }
        
        .field-name {
          flex: 2;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 0.875rem;
          background: white;
          height: 38px;
        }
        
        .field-type {
          flex: 1;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 0.875rem;
          background: white;
          appearance: none;
          height: 38px;
          background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
          background-repeat: no-repeat;
          background-position: right 8px center;
          background-size: 12px;
        }
        
        .remove-field {
          width: 28px;
          height: 38px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .remove-field:hover {
          background: #e2e8f0;
          color: #64748b;
        }
        
        .auto-infer-message {
          border: 1px dashed #cbd5e1;
          background: #f8fafc;
          border-radius: 8px;
          margin: 24px 16px;
          padding: 20px;
          text-align: center;
        }
        
        /* Dark mode schema field styles */
        @media (prefers-color-scheme: dark) {
          .schema-fields {
            border-color: #334155;
            background: #1e293b;
          }
          
          .field-name {
            border-color: #475569;
            background: #1e293b;
            color: #e2e8f0;
          }
          
          .field-type {
            border-color: #475569;
            background: #1e293b;
            color: #e2e8f0;
            background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
          }
          
          .remove-field {
            color: #cbd5e1;
            background: #334155;
            border-color: #475569;
            width: 28px;
            height: 38px;
          }
          
          .remove-field:hover {
            background: #475569;
            color: #f1f5f9;
          }
          
          .auto-infer-message {
            border-color: #475569;
            background: #1e293b;
            padding: 20px;
            text-align: center;
          }
        }
        
        .dark .schema-fields {
          border-color: #334155;
          background: #1e293b;
        }
        
        .dark .field-name {
          border-color: #475569;
          background: #1e293b;
          color: #e2e8f0;
        }
        
        .dark .field-type {
          border-color: #475569;
          background: #1e293b;
          color: #e2e8f0;
          background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
        }
        
        .dark .remove-field {
          color: #cbd5e1;
          background: #334155;
          border-color: #475569;
          width: 28px;
          height: 38px;
        }
        
        .dark .remove-field:hover {
          background: #475569;
          color: #f1f5f9;
        }
        
        .dark .auto-infer-message {
          border-color: #475569;
          background: #1e293b;
          padding: 20px;
          text-align: center;
        }
        
        /* Toggle switch */
        .switch {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 20px;
        }
        
        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: .4s;
        }
        
        .slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: .4s;
        }
        
        input:checked + .slider {
          background-color: #3b82f6;
        }
        
        input:focus + .slider {
          box-shadow: 0 0 1px #3b82f6;
        }
        
        input:checked + .slider:before {
          transform: translateX(20px);
        }
        
        .slider.round {
          border-radius: 34px;
        }
        
        .slider.round:before {
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
}

export default ModelCreation;
